import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { CSVLink } from "react-csv";

import { api, apiEndpoints } from "../../services/api";
import { Input, Label, Table, Loader, Button } from "../../components";
import { useLocation } from "react-router-dom";
import { showSnackbar } from "../../components/snackbar/snackbarSlice";
import { SNACKBAR_TYPES } from "../../components/snackbar/snackbarTypes";
import { selectUsersList, setSelectedUser } from "./manageUsersSlice";
import { downloadAsCSVData } from "../../utils/utils";
import {
  selectCurrentBrand,
  selectIsBrandAdmin,
} from "../../redux/auth/authSlice";

const EXPORT_TYPES = {
  ALL: "all",
  CAMPAIGN_USERS: "campaignUsers",
  CLAIMS: "claims",
};

const ManageUsers = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const account_type = queryParams.get("account_type");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxUsers = useSelector(selectUsersList);
  const isAdmin = useSelector(selectIsBrandAdmin);
  const currentBrand = useSelector(selectCurrentBrand);

  const [searchTerm, setsearchTerm] = useState("");
  const [users, setUsers] = useState(account_type ? [] : reduxUsers);
  const [filteredUsers, setFilteredUsers] = useState(users);
  const [isLoading, setIsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState({
    [EXPORT_TYPES.ALL]: false,
    [EXPORT_TYPES.CAMPAIGN_USERS]: false,
    [EXPORT_TYPES.CLAIMS]: false,
  });

  const columns = [
    { label: "User Id", id: "user_id" },
    { label: "User name", id: "username" },
    { label: "First name", id: "first_name" },
    { label: "Last name", id: "last_name" },
    { label: "Email", id: "email" },
    { label: "Is Active", id: "is_active" },
    { label: "Date Joined", id: "date_joined" },
    { label: "Age Start", id: "age_start" },
    { label: "Age End", id: "age_end" },
    { label: "Device", id: "device" },
    { label: "Is Peloton", id: "is_peleton" },
    { label: "Is Mindbody", id: "is_mindbody" },
    { label: "km/miles", id: "distance_in_km" },
    { label: "Gender", id: "gender" },
    { label: "Timezone", id: "timezone" },
    { label: "Phone Number", id: "phone_number" },
    { label: "Referral Code", id: "referral_code" },
    { label: "User Type", id: "user_type" },
    { label: "How often do Exercise", id: "how_often_do_exercise" },
  ];

  const onSearch = (e) => {
    setsearchTerm(e.target.value);
    const searchString = e.target.value;
    const filteredData = users.filter((value) => {
      if (searchTerm === "") {
        return value;
      } else if (
        value.email.toLowerCase().includes(searchString.toLowerCase()) ||
        value.username.toLowerCase().includes(searchString.toLowerCase()) ||
        value.last_name.toLowerCase().includes(searchString.toLowerCase()) ||
        value.first_name.toLowerCase().includes(searchString.toLowerCase())
      ) {
        return value;
      }
    });
    setFilteredUsers(filteredData);
  };

  const fetchUsersList = async () => {
    if (account_type) {
      setIsLoading((state) => !state);
      const response = await api.get(
        apiEndpoints.getUsersList(account_type, isAdmin && currentBrand)
      );
      setUsers(response);
      // dispatch(saveUsers(response));
      setIsLoading((state) => !state);
    }
  };

  const sendResetPasswordEmail = (email) => {
    const auth = getAuth();
    sendPasswordResetEmail(auth, email)
      .then(() => {
        dispatch(
          showSnackbar({
            message: "Reset Password Email sent successfully",
            type: SNACKBAR_TYPES.SNACKBAR_SUCCESS,
          })
        );
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        dispatch(
          showSnackbar({
            message: errorMessage,
            type: SNACKBAR_TYPES.SNACKBAR_ERROR,
          })
        );

        // ..
      });
  };

  const handleExportCSV = async (exportType) => {
    setExportLoading({ ...exportLoading, [exportType]: true });
    if (exportType === EXPORT_TYPES.ALL) {
      downloadAsCSVData(filteredUsers, "All Users");
    }
    if (exportType === EXPORT_TYPES.CAMPAIGN_USERS) {
      const data = await api.get(apiEndpoints.campaignUsers);
      downloadAsCSVData(data, "Campaign Users");
    }
    if (exportType === EXPORT_TYPES.CLAIMS) {
      const data = await api.get(apiEndpoints.claims);
      downloadAsCSVData(data, "Claims");
    }
    setExportLoading({ ...exportLoading, [exportType]: false });
  };

  useEffect(() => {
    fetchUsersList();
  }, []);

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  if (isLoading) {
    return <Loader />;
  }
  // const all_columns = [;
  return (
    <Box>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: "row",
          marginBottom: "20px",
          marginTop: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "24px",
            width: "fit-content",
          }}
        >
          <Label id="demo-simple-select-label" label="Search" />
          <Input
            onChange={onSearch}
            sx={{
              width: 200,
              marginLeft: "16px",
            }}
            value={searchTerm}
          />
        </Box>
        <Box sx={{ display: "flex" }}>
          <Button
            isLoading={exportLoading[EXPORT_TYPES.ALL]}
            styles={{ margin: "0 16px", fontWeight: 700, fontSize: "16px" }}
            variant="outlined"
            onClick={() => {
              handleExportCSV(EXPORT_TYPES.ALL);
            }}
            label="Export All Users"
          />
          {!isAdmin && (
            <>
              <Button
                isLoading={exportLoading[EXPORT_TYPES.CAMPAIGN_USERS]}
                styles={{ margin: "0 32px", fontWeight: 700, fontSize: "16px" }}
                variant="outlined"
                onClick={() => {
                  handleExportCSV(EXPORT_TYPES.CAMPAIGN_USERS);
                }}
                label="Export Campaign Users"
              />
              <Button
                isLoading={exportLoading[EXPORT_TYPES.CLAIMS]}
                styles={{ margin: "0 40px", fontWeight: 700, fontSize: "16px" }}
                variant="outlined"
                onClick={() => {
                  handleExportCSV(EXPORT_TYPES.CLAIMS);
                }}
                label="Export Claims"
              />
            </>
          )}
        </Box>
      </div>
      <Table
        defaultSortColumn="username"
        defaultSortOrder="asc"
        clickable
        columns={columns}
        onClick={(row) => {
          dispatch(setSelectedUser(row));
          navigate(`/manage-users/${row.user_id}`);
        }}
        rows={[...filteredUsers]}
        totalCount={filteredUsers.length}
        pageCount={10}
        menuOptions={[
          {
            label: "Send Reset Password mail",
            onClick: (row) => {
              sendResetPasswordEmail(row.email);
            },
          },
        ]}
        searchIncluded
      />
    </Box>
  );
};

export default ManageUsers;
